import React, { useEffect, useMemo, useState } from "react";
import api from "../api";

export default function StepHistory({ instanceId, onOpenDetails }) {
  const [history, setHistory] = useState([]);
  const [responsibles, setResponsibles] = useState({});
  const [stepNames, setStepNames] = useState({});
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);

  const parseResponsible = (entry) => {
    if (!entry || typeof entry !== 'object') {
      return { userIds: [], groupIds: [], sectorIds: [] };
    }
    const userIds = new Set();
    const groupIds = new Set();
    const sectorIds = new Set();
    const add = (value, target) => {
      if (typeof value === 'number' && Number.isFinite(value)) target.add(value);
      if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) target.add(parsed);
      }
    };
    const addMany = (values, target) => {
      if (!Array.isArray(values)) return;
      values.forEach((value) => add(value, target));
    };

    add(entry.userId, userIds);
    add(entry.groupId, groupIds);
    add(entry.sectorId, sectorIds);
    addMany(entry.userIds, userIds);
    addMany(entry.groupIds, groupIds);
    addMany(entry.sectorIds, sectorIds);

    return {
      userIds: Array.from(userIds),
      groupIds: Array.from(groupIds),
      sectorIds: Array.from(sectorIds),
    };
  };

  const getResponsibleName = (step) => {
    const entry = responsibles[String(step)];
    if (!entry) return null;
    const { userIds, groupIds, sectorIds } = parseResponsible(entry);
    const parts = [];
    userIds.forEach((id) => {
      const u = users.find((userItem) => userItem.id === id);
      parts.push(u ? `Usuário: ${u.name}` : `Usuário #${id}`);
    });
    groupIds.forEach((id) => {
      const g = groups.find((groupItem) => groupItem.id === id);
      parts.push(g ? `Grupo: ${g.name}` : `Grupo #${id}`);
    });
    sectorIds.forEach((id) => {
      const sector = sectors.find((item) => item.id === id);
      parts.push(sector ? `Setor: ${sector.name}` : `Setor #${id}`);
    });
    if (!parts.length) return null;
    return parts.join(', ');
  };

  const usersById = useMemo(() => {
    const map = new Map();
    users.forEach((userItem) => {
      if (userItem && typeof userItem.id !== 'undefined') {
        map.set(Number(userItem.id), userItem);
      }
    });
    return map;
  }, [users]);

  const getPerformerLabel = (entry) => {
    if (!entry || typeof entry !== 'object') return null;
    const directName = typeof entry.userName === 'string' ? entry.userName.trim() : '';
    if (directName) return directName;

    const rawUserId = entry.userId ?? entry.data?.userId;
    const numericId = Number(rawUserId);
    if (Number.isFinite(numericId)) {
      const match = usersById.get(numericId);
      if (match?.name) {
        return match.name;
      }
      return `Usuário #${numericId}`;
    }
    return null;
  };

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await api.get(
          `/processes/instances/${instanceId}/history`
        );
        setHistory(res.data.steps || []);
        setResponsibles(res.data.stepResponsibles || {});
        setStepNames(res.data.stepNames || {});
      } catch {
        setHistory([]);
        setResponsibles({});
        setStepNames({});
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [instanceId]);

  useEffect(() => {
    api.get('/users').then((res) => setUsers(res.data)).catch(() => setUsers([]));
    api.get('/groups').then((res) => setGroups(res.data)).catch(() => setGroups([]));
    api.get('/sectors').then((res) => setSectors(res.data)).catch(() => setSectors([]));
  }, []);

  if (loading) return <p>Carregando...</p>;
  if (history.length === 0) return <p>Nenhuma etapa executada.</p>;

  const formatEntryLabel = (entry) => {
    const dateLabel = entry.date ? ` (${new Date(entry.date).toLocaleString()})` : '';
    if (entry.eventId && entry.eventId !== 'mail.notification') {
      return `Evento ${entry.eventId}${dateLabel}`;
    }
    const name = stepNames[String(entry.step)];
    const normalizedName =
      typeof name === 'string' && name.trim() ? name.trim() : '';
    const label = normalizedName || 'Etapa';
    return `${label}${dateLabel}`;
  };

  const normalizeMailData = (entry) => {
    const data = entry && typeof entry === 'object' ? entry.data : null;
    if (!data || typeof data !== 'object') return {};
    return data;
  };

  return (
    <div className="space-y-2">
      {history.map((h, idx) => {
        const performer = getPerformerLabel(h);
        return (
          <div key={idx} className="border rounded p-3 bg-gray-50 flex items-center justify-between gap-4">
            <div className="flex-1 space-y-1">
              {h.eventId === 'mail.notification' ? (
                (() => {
                const data = normalizeMailData(h);
                const message =
                  typeof data.message === 'string' && data.message.trim()
                    ? data.message.trim()
                    : 'Notificação de e-mail';
                const status = typeof data.status === 'string' ? data.status : '';
                const contextLabel = (() => {
                  if (data.context === 'assignment') return 'Nova tarefa';
                  if (data.context === 'step_available') return 'Nova etapa disponível';
                  if (data.context === 'responsible_update') return 'Atualização para responsáveis';
                  return null;
                })();
                const stepLabel =
                  typeof data.stepName === 'string' && data.stepName.trim()
                    ? data.stepName.trim()
                    : null;
                const recipient =
                  typeof data.recipient === 'string' && data.recipient.trim()
                    ? data.recipient.trim()
                    : null;
                const subject =
                  typeof data.subject === 'string' && data.subject.trim()
                    ? data.subject.trim()
                    : null;
                const errorMessage =
                  typeof data.error === 'string' && data.error.trim()
                    ? data.error.trim()
                    : null;
                const statusClass =
                  status === 'success'
                    ? 'text-emerald-600'
                    : status === 'error'
                    ? 'text-rose-600'
                    : '';
                const timestamp = h.date ? new Date(h.date).toLocaleString() : '';
                const contextParts = [];
                if (contextLabel) contextParts.push(contextLabel);
                if (stepLabel) contextParts.push(stepLabel);
                const contextText = contextParts.join(' · ');
                const metaParts = [];
                if (recipient) metaParts.push(`Destinatário: ${recipient}`);
                if (subject) metaParts.push(`Assunto: ${subject}`);
                const metaText = metaParts.join(' · ');

                return (
                  <div className="space-y-1">
                    <p className={`text-sm font-medium ${statusClass}`.trim()}>{message}</p>
                    {timestamp && (
                      <p className="text-xs text-slate-500">{timestamp}</p>
                    )}
                    {contextText && (
                      <p className="text-xs text-slate-500">{contextText}</p>
                    )}
                    {metaText && (
                      <p className="text-xs text-slate-500">{metaText}</p>
                    )}
                    {errorMessage && (
                      <p className="text-xs text-rose-600">Erro: {errorMessage}</p>
                    )}
                  </div>
                );
              })()
            ) : (
              <>
                <p className="text-sm text-slate-700">
                  {formatEntryLabel(h)}
                  {h.deadline && (
                    <>
                      {" - prazo: "}
                      <span className={new Date(h.date) > new Date(h.deadline) ? 'text-red-600' : ''}>
                        {new Date(h.deadline).toLocaleString()}
                      </span>
                    </>
                  )}
                  {!h.eventId && getResponsibleName(h.step) && (
                    <>
                      {" - "}
                      {getResponsibleName(h.step)}
                    </>
                  )}
                </p>
                {performer && (
                  <p className="text-xs text-slate-500">Executado por {performer}</p>
                )}
              </>
            )}
            </div>
            {onOpenDetails && (
              <button
                onClick={() => onOpenDetails(idx)}
                className="text-blue-600"
              >
                Detalhes
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
